import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api_data } from "../../services/api_config";
import { useMyContext } from "../../store/ContextApi";
import { FiCalendar, FiUserX, FiChevronRight, FiClock } from "react-icons/fi";

const PastTimesheets = () => {
    const [pastTimesheets, setPastTimesheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notRegistered, setNotRegistered] = useState(false);
    const { logout } = useMyContext();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPastTimesheets = async () => {
            try {
                const response = await api_data.get("/ts/api/timesheets/past");
                const sorted = [...response.data].sort((a, b) => {
                    if (b.year !== a.year) return b.year - a.year;
                    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                    return months.indexOf(b.monthName) - months.indexOf(a.monthName);
                });
                setPastTimesheets(sorted);
            } catch (error) {
                console.error("Error fetching past timesheets:", error);
                if (error.response?.status === 403) {
                    setNotRegistered(true);
                } else if (error.response?.status === 401) {
                    logout();
                    navigate("/login");
                }
            } finally {
                setLoading(false);
            }
        };
        fetchPastTimesheets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (notRegistered) {
        return (
            <div className="page-shell flex items-center justify-center px-4">
                <div className="surface-card max-w-md w-full p-8 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10">
                        <FiUserX className="h-8 w-8 text-amber-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-theme-text mb-2">Not Registered Yet</h2>
                    <p className="text-theme-text-secondary mb-1">
                        Your account is not yet set up as an employee in the payroll system.
                    </p>
                    <p className="text-theme-muted text-sm">
                        Please wait until HR completes your employee setup. Once done, your past timesheets will appear here automatically.
                    </p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-shell">
                <div className="page-container">
                    <div className="mb-6 h-8 w-48 animate-pulse rounded-lg bg-theme-skeleton" />
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="surface-card h-16 animate-pulse bg-theme-surface-muted" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <div className="page-container max-w-3xl">
                <div className="mb-6">
                    <p className="text-sm font-medium text-brand-600">Archive</p>
                    <h1 className="mt-1 text-2xl font-bold tracking-tight text-theme-text sm:text-3xl">
                        Past Timesheets
                    </h1>
                    <p className="mt-1 text-sm text-theme-muted">
                        Review and open previous payroll periods
                    </p>
                </div>

                {pastTimesheets.length === 0 ? (
                    <div className="surface-card flex flex-col items-center justify-center py-16 px-6 text-center">
                        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-theme-empty-icon">
                            <FiClock className="h-7 w-7 text-theme-faint" />
                        </div>
                        <p className="text-lg font-medium text-theme-text-secondary">No past timesheets</p>
                        <p className="mt-1 text-sm text-theme-muted">Submitted timesheets will appear here.</p>
                    </div>
                ) : (
                    <div className="surface-card divide-y divide-theme-border-subtle overflow-hidden">
                        {pastTimesheets.map((ts) => (
                            <Link
                                key={ts.payrollCycleId}
                                to={`/timesheets/${ts.payrollCycleId}`}
                                className="group flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-theme-accent sm:px-6"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-theme-accent text-brand-600 transition-colors group-hover:bg-theme-brand-subtle">
                                        <FiCalendar className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <span className="block text-base font-semibold text-theme-text">
                                            {ts.monthName} {ts.year}
                                        </span>
                                        <span className="text-xs text-theme-muted">Payroll period</span>
                                    </div>
                                </div>
                                <FiChevronRight className="h-5 w-5 text-theme-faint transition-colors group-hover:text-brand-500" />
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PastTimesheets;
